import Alert from "../components/ds/Alert";
import LinkPreviewCard, { LinkifiedText, findLinks } from "../components/LinkPreviewCard";
import Avatar from "../components/ds/Avatar";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";
import Menu, { MenuItem } from "../components/ds/Menu";
import ViewTabs from "../components/ds/ViewTabs";
import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useOpenSpace } from "../context/SpaceGateContext";
import { useTranslation } from "../context/TranslationContext";
import { getAllMyMintSpaces } from "../api/mintSpace";
import { createAnnouncement, getMyAnnouncementsFeed, getAnnouncement, toggleAnnouncementLike, voteOnAnnouncementPoll, deleteAnnouncement } from "../api/announcements";
import { uploadImage, uploadVideo } from "../api/upload";
import { FiTrash2, FiLoader, FiBarChart2, FiX, FiPlus } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import CustomVideoPlayer from "../components/CustomVideoPlayer";
import CampaignAnnouncementCard from "../components/CampaignAnnouncementCard";
import PostComments from "../components/comments/PostComments";
import FullScreenLayer from "../components/shell/FullScreenLayer";
import { useIsDesktop } from "../components/shell/useShellData";
import { getProfileAvatarUrl } from "../utils/userDisplay";

/** The two home feeds, in the order the tabs show them. */
const FEEDS = ["following", "forYou"];
const FEED_KEY = "homeFeed";

const savedFeed = () => {
  try {
    const saved = localStorage.getItem(FEED_KEY);
    return FEEDS.includes(saved) ? saved : "forYou";
  } catch {
    return "forYou";
  }
};

const POST_ACTION = "flex items-center gap-space-8 text-footnote text-label-secondary transition-colors hover:text-label-primary";

/** Below desktop width the post form fills the screen, opened from the tab bar's +. */
const FullScreenComposer = ({ isDark, onClose, children }) => {
  const { t } = useTranslation();
  return (
    <FullScreenLayer isDark={isDark} label={t("navigation.createPost")}>
      <header className="flex h-[60px] shrink-0 items-center gap-space-4 border-b border-separator-default px-space-8">
        <IconButton size="m" variant="plain" label={t("common.close")} onClick={onClose}>
          <Icon name="close" size={22} />
        </IconButton>
        <h2 className="text-headline">{t("navigation.createPost")}</h2>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
    </FullScreenLayer>
  );
};

const ActualHomePage = ({ isDark, setToast }) => {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const desktop = useIsDesktop();
  const openSpace = useOpenSpace();
  const [searchParams, setSearchParams] = useSearchParams();
  const composing = !desktop && searchParams.get("compose") === "1";
  const [announcements, setAnnouncements] = useState([]);
  const [feed, setFeed] = useState(savedFeed);
  const [feedLoading, setFeedLoading] = useState(true);
  const [myMintSpaces, setMyMintSpaces] = useState([]);
  const [spacesLoaded, setSpacesLoaded] = useState(false);
  const [selectedMintSpace, setSelectedMintSpace] = useState(null);
  const [newAnnouncement, setNewAnnouncement] = useState("");
  const [newAnnouncementImage, setNewAnnouncementImage] = useState(null);
  const [newAnnouncementImagePreview, setNewAnnouncementImagePreview] = useState(null);
  const [newAnnouncementVideo, setNewAnnouncementVideo] = useState(null);
  const [newAnnouncementVideoPreview, setNewAnnouncementVideoPreview] = useState(null);
  const [postingAnnouncement, setPostingAnnouncement] = useState(false);
  const [showMintSpaceDropdown, setShowMintSpaceDropdown] = useState(false);
  const closeMintSpaceDropdown = useCallback(() => setShowMintSpaceDropdown(false), []);
  const [showWelcome, setShowWelcome] = useState(false);
  const [openComments, setOpenComments] = useState({});
  // The post (and comment) a notification linked to: kept on top of the feed.
  const [focus, setFocus] = useState(null);
  const focusPostIdRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pollDraft, setPollDraft] = useState(null);
  const dropdownRef = useRef(null);

  const isMyPost = (announcement) => {
    if (!user) return false;
    const a = announcement?.author;
    const authorId = typeof a === "string" ? a : a?._id || a;
    return authorId?.toString() === user.id;
  };

  const mintSpaceIdFromAnnouncement = (announcement) =>
    announcement?.mintSpace?._id || announcement?.mintSpace || selectedMintSpace?._id || null;

  // Owners and moderators pin comments; myMintSpaces is exactly the spaces they run.
  const canPinOn = (announcement) => {
    const spaceId = String(announcement?.mintSpace?._id || announcement?.mintSpace || "");
    return !!spaceId && myMintSpaces.some((ms) => String(ms._id) === spaceId);
  };

  const chooseFeed = (next) => {
    if (next === feed) return;
    try {
      localStorage.setItem(FEED_KEY, next);
    } catch {
      /* the choice just isn't remembered */
    }
    focusPostIdRef.current = null;
    setFocus(null);
    setAnnouncements([]);
    setFeed(next);
  };

  const replaceAnnouncement = (next) =>
    setAnnouncements((prev) => prev.map((a) => (a._id === next._id ? next : a)));

  const confirmDeleteAnnouncement = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const target = announcements.find((a) => a._id === deleteTarget);
      const response = await deleteAnnouncement(deleteTarget, mintSpaceIdFromAnnouncement(target));
      if (response.success) {
        setAnnouncements((prev) => prev.filter((a) => a._id !== deleteTarget));
        setToast?.(t("toasts.announcementDeleted") || "Post deleted");
        setDeleteTarget(null);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      setToast?.(t("toasts.failedDeleteAnnouncement") || "Failed to delete post");
    } finally {
      setIsDeleting(false);
    }
  };

  const userVotedIndex = (post) => {
    if (!post?.poll || !user) return -1;
    return post.poll.options.findIndex((opt) =>
      (opt.votes || []).some((v) => {
        const vid = typeof v === "string" ? v : v?._id || v;
        return vid?.toString() === user.id;
      })
    );
  };
  const pollTotalVotes = (post) =>
    post?.poll ? post.poll.options.reduce((sum, o) => sum + (o.votes?.length || 0), 0) : 0;

  const handleVoteAnnouncement = async (postId, optionIndex) => {
    try {
      const target = announcements.find((a) => a._id === postId);
      const response = await voteOnAnnouncementPoll(
        postId,
        optionIndex,
        mintSpaceIdFromAnnouncement(target)
      );
      if (response.success) {
        setAnnouncements((prev) => prev.map((a) => (a._id === postId ? response.data : a)));
      }
    } catch (error) {
      console.error("Error voting:", error);
      setToast?.(error?.message || "Failed to vote");
    }
  };

  const toggleAnnouncementComments = (postId) => {
    setOpenComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleShareAnnouncement = async (announcement) => {
    const mintSpaceId =
      announcement.mintSpace?._id || announcement.mintSpace || null;
    if (!mintSpaceId) {
      setToast?.(t("common.copyFailed") || "Cannot share this post");
      return;
    }
    const url = `${window.location.origin}/mint-space/${mintSpaceId}?nav=home&post=${announcement._id}`;
    const title =
      announcement.author?.username ||
      announcement.author?.businessName ||
      "Mintly post";
    const text = announcement.content
      ? announcement.content.slice(0, 200)
      : announcement.poll?.question || "Shared post";

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setToast?.(t("common.linkCopied") || "Link copied to clipboard");
    } catch {
      setToast?.(t("common.copyFailed") || "Could not copy link");
    }
  };

  // A notification links to /home?post=<id>&comment=<id>: bring that post to the top
  // with its comments open, whether or not the feed would have shown it.
  useEffect(() => {
    const postId = searchParams.get("post");
    if (!isAuthenticated || !postId) return;
    const commentId = searchParams.get("comment");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("post");
        next.delete("comment");
        return next;
      },
      { replace: true }
    );
    getAnnouncement(postId)
      .then((response) => {
        if (!response?.success) return;
        focusPostIdRef.current = postId;
        setAnnouncements((prev) => [response.data, ...prev.filter((a) => a._id !== postId)]);
        setOpenComments((prev) => ({ ...prev, [postId]: true }));
        setFocus({ postId, commentId });
        if (!commentId) window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch(() => setToast?.(t("comments.postGone")));
  }, [isAuthenticated, searchParams, setSearchParams, setToast, t]);

  // Check if this is the first load after signup
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    const welcomeKey = `welcomeShown_${user.id}`;
    const hasSeenWelcome = localStorage.getItem(welcomeKey);
    
    if (!hasSeenWelcome) {
      setShowWelcome(true);
      // Mark as seen after showing
      localStorage.setItem(welcomeKey, 'true');
    }
  }, [isAuthenticated, user]);

  const mutedText = "text-label-secondary";
  const baseText = "text-label-primary";
  const feedSurface = "border-separator-default bg-bg-raised";
  const divider = "border-separator-default";
  const inputClass = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";
  // Fetch user's mint spaces
  useEffect(() => {
    const fetchMintSpaces = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await getAllMyMintSpaces();
        if (response.success && response.data) {
          // Filter to owned or moderator mint spaces (for posting announcements)
          const canPost = response.data.filter(ms => ms.isOwner || ms.isModerator);
          setMyMintSpaces(canPost);
          if (canPost.length > 0 && !selectedMintSpace) {
            setSelectedMintSpace(canPost[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching mint spaces:", error);
      } finally {
        setSpacesLoaded(true);
      }
    };

    fetchMintSpaces();
  }, [isAuthenticated, selectedMintSpace]);

  // Fetch the chosen feed
  useEffect(() => {
    let cancelled = false;
    setFeedLoading(true);
    const fetchAnnouncements = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await getMyAnnouncementsFeed(feed);
        // A switch to the other feed since this was asked for: drop it.
        if (cancelled) return;
        if (response.success) {
          const feed = response.data || [];
          setAnnouncements((prev) => {
            const focusId = focusPostIdRef.current;
            const focused = focusId && (feed.find((a) => a._id === focusId) || prev.find((a) => a._id === focusId));
            return focused ? [focused, ...feed.filter((a) => a._id !== focusId)] : feed;
          });
        }
      } catch (error) {
        console.error("Error fetching announcements:", error);
      } finally {
        if (!cancelled) setFeedLoading(false);
      }
    };

    fetchAnnouncements();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAnnouncements, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, feed]);

  // Close dropdown when clicking outside
  const handleAnnouncementImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setToast(t("actualHome.imageSizeMustBeLessThan10MB"));
        return;
      }
      // Clear video and poll if image is selected
      setPollDraft(null);
      setNewAnnouncementVideo(null);
      setNewAnnouncementVideoPreview(null);
      setNewAnnouncementImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewAnnouncementImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAnnouncementVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        setToast(t("actualHome.videoSizeMustBeLessThan100MB"));
        return;
      }
      // Clear image and poll if video is selected
      setPollDraft(null);
      setNewAnnouncementImage(null);
      setNewAnnouncementImagePreview(null);
      setNewAnnouncementVideo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewAnnouncementVideoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Back to where the + was pressed; a link straight to the form has nowhere to go back to.
  const closeComposer = () => {
    if (location.state?.composing) navigate(-1);
    else navigate("/home", { replace: true });
  };

  // In the feed on desktop; below that, only the tab bar's + opens it.
  const placeComposer = (form) => {
    if (desktop) return form;
    if (!composing) return null;
    return (
      <FullScreenComposer isDark={isDark} onClose={closeComposer}>
        {form}
      </FullScreenComposer>
    );
  };

  const handlePostAnnouncement = async () => {
    if (!selectedMintSpace) {
      setToast(t("actualHome.pleaseSelectMintSpace"));
      return;
    }

    const hasPoll = !!pollDraft;
    const validPollOptions = hasPoll
      ? pollDraft.options.map((o) => o.trim()).filter((o) => o !== "")
      : [];
    const pollValid = hasPoll && pollDraft.question.trim() !== "" && validPollOptions.length >= 2;

    if (hasPoll && !pollValid) {
      setToast(t("actualHome.pollNeedsQuestionAndOptions") || "Polls need a question and at least 2 options");
      return;
    }

    if (!hasPoll && !newAnnouncement.trim() && !newAnnouncementImagePreview && !newAnnouncementVideoPreview) {
      setToast(t("actualHome.pleaseAddContent"));
      return;
    }

    try {
      setPostingAnnouncement(true);
      let imageUrl = "";
      let videoUrl = "";
      
      if (!hasPoll && newAnnouncementImage) {
        const uploadResponse = await uploadImage(newAnnouncementImage, "announcements");
        if (uploadResponse.success) {
          imageUrl = uploadResponse.url;
        }
      }

      if (!hasPoll && newAnnouncementVideo) {
        const uploadResponse = await uploadVideo(newAnnouncementVideo, "announcements");
        if (uploadResponse.success) {
          videoUrl = uploadResponse.url;
        }
      }

      const pollPayload = pollValid
        ? {
            question: pollDraft.question.trim(),
            options: validPollOptions.map((text) => ({ text })),
          }
        : null;

      const response = await createAnnouncement(
        selectedMintSpace._id,
        newAnnouncement.trim(),
        imageUrl,
        videoUrl,
        null,
        "announcement",
        pollPayload
      );

      if (response.success) {
        // Following holds other people's posts; your own shows under For you.
        if (feed === "forYou") setAnnouncements([response.data, ...announcements]);
        setNewAnnouncement("");
        setNewAnnouncementImage(null);
        setNewAnnouncementImagePreview(null);
        setNewAnnouncementVideo(null);
        setNewAnnouncementVideoPreview(null);
        setPollDraft(null);
        setToast(t("actualHome.announcementPostedSuccessfully"));
        if (composing) closeComposer();
      }
    } catch (error) {
      console.error("Error posting announcement:", error);
      setToast(error.message || t("actualHome.failedToPostAnnouncement"));
    } finally {
      setPostingAnnouncement(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("messages.justNow");
    if (diffMins < 60) return t("messages.activeMinutesAgo", { minutes: diffMins });
    if (diffHours < 24) return t("messages.activeHoursAgo", { hours: diffHours });
    if (diffDays < 7) return t("messages.activeDaysAgo", { days: diffDays });
    const monthNames = [
      t("common.monthJan"), t("common.monthFeb"), t("common.monthMar"),
      t("common.monthApr"), t("common.monthMay"), t("common.monthJun"),
      t("common.monthJul"), t("common.monthAug"), t("common.monthSep"),
      t("common.monthOct"), t("common.monthNov"), t("common.monthDec")
    ];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return t("common.dateFormat", { month, day, year });
  };

  const getAuthorName = (author) => {
    if (typeof author === 'string') return author;
    return author.username || author.businessName || t("common.unknown");
  };

  const getAuthorAvatar = (author) => {
    if (typeof author === 'string') return null;
    return author.profile?.avatarImage || author.profile?.avatar || null;
  };

  const handleLikeAnnouncement = async (announcementId) => {
    const snapshot = announcements;
    setAnnouncements((prev) =>
      prev.map((ann) => {
        if (ann._id !== announcementId) return ann;
        const likes = ann.likes || [];
        const liked = likes.some((l) => {
          const id = typeof l === "string" ? l : l?._id || l;
          return id?.toString() === user?.id;
        });
        const nextLikes = liked
          ? likes.filter((l) => {
              const id = typeof l === "string" ? l : l?._id || l;
              return id?.toString() !== user?.id;
            })
          : [...likes, user?.id];
        return { ...ann, likes: nextLikes };
      })
    );
    try {
      const target = announcements.find((a) => a._id === announcementId);
      const response = await toggleAnnouncementLike(
        announcementId,
        mintSpaceIdFromAnnouncement(target)
      );
      if (response.success) {
        setAnnouncements((prev) =>
          prev.map((ann) => (ann._id === announcementId ? response.data : ann))
        );
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      setAnnouncements(snapshot);
      setToast(t("actualHome.failedToUpdateLike"));
    }
  };

  const isLiked = (announcement, userId) => {
    if (!userId || !announcement.likes) return false;
    return announcement.likes.some((likeId) => {
      const likeUserId = typeof likeId === 'string' ? likeId : (likeId._id || likeId);
      return likeUserId.toString() === userId;
    });
  };

  return (
    <div className="home-feed mx-auto w-full min-w-0 max-w-[720px] font-ds">
      {/* Welcome splash — first visit only */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`mb-4 overflow-hidden rounded-radius-md border px-6 py-8 text-center sm:py-10 ${feedSurface}`}
          >
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-accent-default text-title-1 text-accent-on">
              M
            </div>
            <h2 className={`text-title-2 sm:text-2xl ${baseText}`}>
              {t("actualHome.welcomeToMintly")}
            </h2>
            <p className={`mx-auto mt-2 max-w-md text-subheadline ${mutedText}`}>
              {t("actualHome.welcomeSubtitle")}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {isAuthenticated && (
        <div className="flex flex-col gap-space-24">
          <ViewTabs
            label={t("actualHome.feed_following")}
            value={feed}
            onChange={chooseFeed}
            options={FEEDS.map((key) => ({ value: key, label: t(`actualHome.feed_${key}`) }))}
          />

          {/* Composer */}
          {placeComposer(myMintSpaces.length > 0 ? (
            <div
              className={`flex flex-col gap-space-12 p-space-16 ${
                composing ? "min-h-full pb-[max(1rem,env(safe-area-inset-bottom))]" : "rounded-radius-lg bg-bg-raised"
              }`}
            >
              <div className="relative min-w-0 self-start" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowMintSpaceDropdown(!showMintSpaceDropdown)}
                  className="inline-flex h-[40px] max-w-full items-center gap-space-8 rounded-[22px] bg-fill-secondary pl-[14px] pr-[10px] text-subheadline-semibold text-label-primary"
                >
                  <span className="truncate">{selectedMintSpace?.name || t("actualHome.selectMintSpace")}</span>
                  <Icon
                    name={showMintSpaceDropdown ? "chevron-up" : "chevron-down"}
                    size={16}
                    className="shrink-0 text-label-secondary"
                  />
                </button>

                <Menu
                  open={showMintSpaceDropdown}
                  onClose={closeMintSpaceDropdown}
                  anchorRef={dropdownRef}
                  width={280}
                  align="start"
                  label={t("actualHome.selectMintSpace")}
                >
                  {myMintSpaces.map((ms) => (
                    <MenuItem
                      key={ms._id}
                      leading={<Avatar src={ms.logo} name={ms.name} />}
                      selected={selectedMintSpace?._id === ms._id}
                      onClick={() => setSelectedMintSpace(ms)}
                    >
                      {ms.name}
                    </MenuItem>
                  ))}
                </Menu>
              </div>

              <div className={`flex gap-space-12 ${composing ? "flex-1" : ""}`}>
                <Avatar size="m" src={getProfileAvatarUrl(user)} name={user?.username || user?.businessName || "U"} />
                <textarea
                  autoFocus={composing}
                  value={newAnnouncement}
                  onChange={(e) => setNewAnnouncement(e.target.value)}
                  placeholder={t("actualHome.whatsOnYourMind")}
                  rows={1}
                  className="min-h-[64px] min-w-0 flex-1 resize-none rounded-[22px] bg-fill-quaternary p-space-16 text-body text-label-primary outline-none placeholder:text-label-tertiary focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
                />
              </div>

              <div className="flex min-w-0 flex-col gap-space-12">
                    {pollDraft && (
                      <div
                        className={`overflow-hidden rounded-radius-sm border ${
                          isDark ? "border-accent-default bg-mint/[0.04]" : "border-accent-default bg-mint/[0.03]"
                        }`}
                      >
                        <div
                          className={`flex items-center justify-between gap-2 border-b px-3 py-2.5 ${
                            "border-separator-default bg-bg-raised"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="flex size-7 items-center justify-center rounded-radius-xs bg-accent-default text-accent-on">
                              <FiBarChart2 className="size-3.5" />
                            </span>
                            <span className={`text-subheadline-semibold ${baseText}`}>
                              {t("campaigns.poll") || "Poll"}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setPollDraft(null)}
                            className={`flex size-7 items-center justify-center rounded-radius-xs transition ${
                              "text-slate-400 hover:text-status-danger"
                            }`}
                            aria-label={t("common.remove") || "Remove poll"}
                          >
                            <FiX className="size-4" />
                          </button>
                        </div>
                        <div className="space-y-2.5 p-3">
                          <input
                            value={pollDraft.question}
                            onChange={(e) => setPollDraft((p) => ({ ...p, question: e.target.value }))}
                            placeholder={t("campaigns.pollQuestionPlaceholder") || "Ask a question…"}
                            maxLength={280}
                            className={`w-full rounded-radius-sm border px-3 py-2.5 text-subheadline-semibold outline-none focus:ring-1 focus:ring-accent-default ${inputClass}`}
                          />
                          {pollDraft.options.map((opt, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span
                                className={`flex size-6 shrink-0 items-center justify-center rounded-full text-caption-1-semibold ${
                                  "bg-fill-tertiary text-label-secondary"
                                }`}
                              >
                                {idx + 1}
                              </span>
                              <input
                                value={opt}
                                onChange={(e) =>
                                  setPollDraft((p) => {
                                    const next = [...p.options];
                                    next[idx] = e.target.value;
                                    return { ...p, options: next };
                                  })
                                }
                                placeholder={`${t("campaigns.option") || "Option"} ${idx + 1}`}
                                maxLength={120}
                                className={`min-w-0 flex-1 rounded-radius-sm border px-3 py-2 text-subheadline outline-none focus:ring-1 focus:ring-accent-default ${inputClass}`}
                              />
                              {pollDraft.options.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPollDraft((p) => ({
                                      ...p,
                                      options: p.options.filter((_, i) => i !== idx),
                                    }))
                                  }
                                  className={`flex size-7 shrink-0 items-center justify-center rounded-radius-xs ${mutedText} hover:text-status-danger`}
                                  aria-label={t("common.remove") || "Remove option"}
                                >
                                  <FiX className="size-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                          {pollDraft.options.length < 10 ? (
                            <button
                              type="button"
                              onClick={() => setPollDraft((p) => ({ ...p, options: [...p.options, ""] }))}
                              className={`flex w-full items-center justify-center gap-1.5 rounded-radius-sm border border-dashed py-2 text-subheadline font-medium transition ${
                                isDark
                                  ? "border-white/15 text-slate-300 hover:border-accent-default hover:text-accent-text"
                                  : "border-slate-300 text-slate-500 hover:border-accent-default hover:text-accent-text"
                              }`}
                            >
                              <FiPlus className="size-4" />
                              {t("campaigns.addOption") || "Add option"}
                            </button>
                          ) : (
                            <p className={`text-center text-footnote ${mutedText}`}>
                              {t("campaigns.maxOptionsReached") || "Maximum of 10 options"}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {(newAnnouncementImagePreview || newAnnouncementVideoPreview) && (
                      <div className="relative overflow-hidden rounded-radius-sm">
                        {newAnnouncementImagePreview && (
                          <img src={newAnnouncementImagePreview} alt="" className="max-h-80 w-full object-cover" />
                        )}
                        {newAnnouncementVideoPreview && (
                          <CustomVideoPlayer src={newAnnouncementVideoPreview} isDark={isDark} className="max-h-80" />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setNewAnnouncementImage(null);
                            setNewAnnouncementImagePreview(null);
                            setNewAnnouncementVideo(null);
                            setNewAnnouncementVideoPreview(null);
                          }}
                          className="absolute right-2 top-2 rounded-radius-xs bg-black/60 p-2 text-white backdrop-blur-sm transition hover:bg-black/80"
                          aria-label={t("common.remove") || "Remove"}
                        >
                          <FiTrash2 className="size-4" />
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-space-12">
                      <div className="flex items-center gap-space-4">
                        <label className={`flex size-8 cursor-pointer items-center justify-center rounded-full text-label-secondary transition-colors hover:bg-fill-quaternary ${pollDraft ? "pointer-events-none opacity-40" : ""}`}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleAnnouncementImageChange}
                            className="hidden"
                            disabled={!!pollDraft}
                          />
                          <Icon name="image" size={18} />
                        </label>
                        <label className={`flex size-8 cursor-pointer items-center justify-center rounded-full text-label-secondary transition-colors hover:bg-fill-quaternary ${pollDraft ? "pointer-events-none opacity-40" : ""}`}>
                          <input
                            type="file"
                            accept="video/*"
                            onChange={handleAnnouncementVideoChange}
                            className="hidden"
                            disabled={!!pollDraft}
                          />
                          <Icon name="video" size={18} />
                        </label>
                        <IconButton
                          variant="plain"
                          label={t("campaigns.poll") || "Add poll"}
                          selected={Boolean(pollDraft)}
                          onClick={() => {
                            if (pollDraft) {
                              setPollDraft(null);
                            } else {
                              setNewAnnouncementImage(null);
                              setNewAnnouncementImagePreview(null);
                              setNewAnnouncementVideo(null);
                              setNewAnnouncementVideoPreview(null);
                              setPollDraft({ question: "", options: ["", ""] });
                            }
                          }}
                        >
                          <Icon name="chart" size={18} />
                        </IconButton>
                      </div>
                      <Button
                        size="s"
                        icon={<Icon name="send" size={16} />}
                        onClick={handlePostAnnouncement}
                        disabled={!selectedMintSpace}
                        loading={postingAnnouncement}
                      >
                        {postingAnnouncement ? t("actualHome.posting") : t("actualHome.postAnnouncement")}
                      </Button>
                    </div>
              </div>
            </div>
          ) : spacesLoaded ? (
            <EmptyState icon="sparkle" title={t("actualHome.needToOwnMintSpace")} className="rounded-radius-lg bg-bg-raised" />
          ) : null)}

          {/* Feed — single column, one post at a time */}
          {announcements.length > 0 ? (
            announcements.map((announcement, index) => {
              const authorName = getAuthorName(announcement.author);
              const authorAvatar = getAuthorAvatar(announcement.author);
              const mintSpaceName = announcement.mintSpace?.name || t("mintSpace.mintSpace");
              const liked = isLiked(announcement, user?.id);
              const authorId =
                typeof announcement.author === "string"
                  ? announcement.author
                  : announcement.author._id || announcement.author;

              return (
                <motion.article
                  key={announcement._id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.15) }}
                  className="flex flex-col gap-space-12 rounded-radius-lg bg-bg-raised-end p-space-16"
                >
                  {/* Post header */}
                  <div className="flex items-center gap-space-12">
                    <button type="button" onClick={() => navigate(`/profile/${authorId}`)} className="shrink-0 rounded-14" aria-label={authorName}>
                      <Avatar size="m" src={authorAvatar} name={authorName} />
                    </button>
                    <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/profile/${authorId}`)}
                        className="truncate text-left text-subheadline-semibold text-label-primary hover:underline"
                      >
                        {authorName}
                      </button>
                      <div className="flex flex-wrap items-center gap-x-[6px] text-footnote text-label-secondary">
                        {announcement.mintSpace?._id && (
                          <>
                            <button
                              type="button"
                              onClick={() => openSpace(announcement.mintSpace)}
                              className="inline-flex min-w-0 items-center gap-[6px] hover:text-accent-text hover:underline"
                            >
                              <Avatar src={announcement.mintSpace.logo} name={mintSpaceName} />
                              <span className="truncate">{mintSpaceName}</span>
                            </button>
                            <span aria-hidden>·</span>
                          </>
                        )}
                        <time>{formatTimestamp(announcement.createdAt)}</time>
                      </div>
                    </div>
                    {isMyPost(announcement) && (
                      <IconButton variant="plain" label={t("common.delete") || "Delete"} onClick={() => setDeleteTarget(announcement._id)}>
                        <Icon name="more" size={18} />
                      </IconButton>
                    )}
                  </div>

                  {/* Post body */}
                  <div className="flex flex-col gap-space-12">
                    {announcement.content && (
                      <LinkifiedText
                        text={announcement.content}
                        className="whitespace-pre-wrap break-words text-body text-label-primary"
                      />
                    )}

                    {findLinks(announcement.content)[0] && !announcement.image && !announcement.video && (
                      <LinkPreviewCard url={findLinks(announcement.content)[0]} />
                    )}

                    {announcement.campaign && (
                      <CampaignAnnouncementCard
                        campaign={announcement.campaign}
                        isDark={isDark}
                        mintSpaceId={announcement.mintSpace?._id}
                      />
                    )}

                    {announcement.image && (
                      <div className="overflow-hidden rounded-radius-md">
                        <img
                          src={announcement.image}
                          alt=""
                          className="max-h-[32rem] w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}

                    {announcement.video && (
                      <div className="overflow-hidden rounded-radius-md">
                        <CustomVideoPlayer
                          src={announcement.video}
                          isDark={isDark}
                          className="max-h-[32rem]"
                        />
                      </div>
                    )}

                    {announcement.poll && announcement.poll.options?.length > 0 && (() => {
                      const total = pollTotalVotes(announcement);
                      const votedIdx = userVotedIndex(announcement);
                      return (
                        <div className="space-y-2">
                          {announcement.poll.question && (
                            <p className={`text-subheadline-semibold ${baseText}`}>{announcement.poll.question}</p>
                          )}
                          {announcement.poll.options.map((opt, idx) => {
                            const count = opt.votes?.length || 0;
                            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                            const isMyVote = votedIdx === idx;
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleVoteAnnouncement(announcement._id, idx)}
                                className={`relative w-full overflow-hidden rounded-radius-xs border px-3 py-2.5 text-left transition ${
                                  "border-separator-default bg-bg-raised hover:border-accent-default"
                                } ${isMyVote ? "ring-2 ring-accent-default" : ""}`}
                              >
                                <div
                                  className={`absolute inset-y-0 left-0 ${isMyVote ? "bg-accent-tinted" : isDark ? "bg-white/5" : "bg-slate-200/80"}`}
                                  style={{ width: `${pct}%` }}
                                  aria-hidden
                                />
                                <div className="relative flex items-center justify-between gap-2">
                                  <span className={`text-subheadline ${baseText}`}>{opt.text}</span>
                                  <span className={`shrink-0 text-footnote tabular-nums ${mutedText}`}>
                                    {pct}% · {count}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                          <p className={`text-footnote ${mutedText}`}>
                            {total} {total === 1 ? "vote" : "votes"}
                          </p>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-space-24">
                    <button
                      type="button"
                      onClick={() => handleLikeAnnouncement(announcement._id)}
                      aria-pressed={liked}
                      className={`${POST_ACTION} ${liked ? "text-status-danger" : ""}`}
                    >
                      <Icon name="heart" size={18} className={liked ? "fill-current" : ""} />
                      <span>{announcement.likes?.length || 0}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleAnnouncementComments(announcement._id)}
                      aria-expanded={Boolean(openComments[announcement._id])}
                      className={`${POST_ACTION} ${openComments[announcement._id] ? "text-accent-text" : ""}`}
                    >
                      <Icon name="message" size={18} />
                      <span>{announcement.comments?.length || 0}</span>
                    </button>
                    <button type="button" onClick={() => handleShareAnnouncement(announcement)} className={POST_ACTION}>
                      <Icon name="share" size={18} />
                      <span>{t("actualHome.share")}</span>
                    </button>
                  </div>

                  {/* Comments */}
                  <AnimatePresence>
                    {openComments[announcement._id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-separator-default pt-space-12">
                          <PostComments
                            post={announcement}
                            isDark={isDark}
                            canPin={canPinOn(announcement)}
                            mintSpaceId={mintSpaceIdFromAnnouncement(announcement)}
                            onPostChange={replaceAnnouncement}
                            setToast={setToast}
                            highlightCommentId={focus?.postId === announcement._id ? focus.commentId : null}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.article>
              );
            })
          ) : feedLoading ? (
            <div className="flex justify-center px-4 py-16">
              <FiLoader className={`size-6 animate-spin ${mutedText}`} />
            </div>
          ) : feed === "following" ? (
            <EmptyState
              icon="users"
              title={t("actualHome.followingEmpty")}
              message={t("actualHome.followingEmptyHint")}
              actionLabel={t("actualHome.followingEmptyAction")}
              onAction={() => chooseFeed("forYou")}
            />
          ) : (
            <EmptyState icon="message" title={t("actualHome.noAnnouncements")} message={t("actualHome.emptyFeedHint")} />
          )}
        </div>
      )}

      <Alert
        open={!!deleteTarget}
        title={t("toasts.deletePostTitle")}
        message={t("toasts.deletePostBody")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        onConfirm={confirmDeleteAnnouncement}
        onClose={() => setDeleteTarget(null)}
        loading={isDeleting}
        isDark={isDark}
      />
    </div>
  );
};

export default ActualHomePage;

